'use client';

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Calculator, TrendingUp, Building2, User } from 'lucide-react';
import {
  getSavingsTier,
  trackCalculatorResultShown,
  trackCalculatorSubmit,
  trackContactClick,
} from '@/lib/analytics';

interface TaxResult {
  monthlyTax: number;
  yearlyTax: number;
  effectiveRate: number;
}

interface Results {
  pf: TaxResult;
  pj: TaxResult;
  savings: number;
}

// IRPF 2026 - Tabela Progressiva (Lei 15.270/25)
// Faixas de base de cálculo atualizadas
const IRPF_TABLE = [
  { limit: 3036.00, rate: 0, deduction: 0 },
  { limit: 3533.00, rate: 0.075, deduction: 227.70 },
  { limit: 4688.00, rate: 0.15, deduction: 492.68 },
  { limit: 5830.00, rate: 0.225, deduction: 844.28 },
  { limit: Infinity, rate: 0.275, deduction: 1135.78 },
];

// Limites para isenção/redução do IR 2026
const ISENCAO_TOTAL_LIMITE = 5000.00;
const REDUCAO_LIMITE = 7350.00;

// Fórmula de redução para faixa de transição (R$ 5.000 a R$ 7.350)
// Redução = R$ 978,61 − (0,133145 × Renda Tributável)
function calcularReducao2026(rendaTributavel: number): number {
  if (rendaTributavel <= ISENCAO_TOTAL_LIMITE) {
    return Infinity; // Isenção total
  }
  if (rendaTributavel >= REDUCAO_LIMITE) {
    return 0; // Sem redução
  }
  const reducao = 978.61 - (0.133145 * rendaTributavel);
  return Math.max(0, reducao);
}

// PJ - Lucro Presumido para locação de imóveis
const PJ_RATES = {
  presumption: 0.32, // 32% presunção para serviços
  irpj: 0.15,        // 15% IRPJ sobre lucro presumido
  csll: 0.09,        // 9% CSLL sobre lucro presumido
  pis: 0.0065,       // 0,65% PIS sobre receita bruta
  cofins: 0.03,      // 3% COFINS sobre receita bruta
};

// Calcula o imposto bruto pela tabela progressiva (sem a redução 2026)
function calcularImpostoBruto(rendaTributavel: number): number {
  if (rendaTributavel <= 0) return 0;
  const bracket = IRPF_TABLE.find(b => rendaTributavel <= b.limit) || IRPF_TABLE[IRPF_TABLE.length - 1];
  return Math.max(0, (rendaTributavel * bracket.rate) - bracket.deduction);
}

// Encontra a alíquota marginal baseada na renda total
function getAliquotaMarginal(rendaTotal: number): number {
  const bracket = IRPF_TABLE.find(b => rendaTotal <= b.limit) || IRPF_TABLE[IRPF_TABLE.length - 1];
  return bracket.rate;
}

// Calcula o imposto PF sobre o aluguel considerando a renda tributável total
// Usa diferença de impostos para capturar benefícios da redução 2026,
// mas limita ao máximo da alíquota marginal pura (evita cliff effect)
function calculatePF(monthlyRent: number, totalIncomeWithRent?: number): TaxResult {
  if (monthlyRent <= 0) {
    return { monthlyTax: 0, yearlyTax: 0, effectiveRate: 0 };
  }

  let monthlyTax: number;

  if (totalIncomeWithRent && totalIncomeWithRent >= monthlyRent) {
    // Renda total informada

    // Se renda total está totalmente isenta, aluguel não paga imposto
    if (totalIncomeWithRent <= ISENCAO_TOTAL_LIMITE) {
      monthlyTax = 0;
    } else {
      const rendaSemAluguel = totalIncomeWithRent - monthlyRent;

      // Imposto líquido sobre renda total (com redução 2026 se aplicável)
      const impostoBrutoTotal = calcularImpostoBruto(totalIncomeWithRent);
      const reducaoTotal = calcularReducao2026(totalIncomeWithRent);
      const impostoLiquidoTotal = reducaoTotal === Infinity ? 0 : Math.max(0, impostoBrutoTotal - reducaoTotal);

      // Imposto líquido sobre renda sem aluguel (com redução 2026 se aplicável)
      const impostoBrutoSemAluguel = calcularImpostoBruto(rendaSemAluguel);
      const reducaoSemAluguel = calcularReducao2026(rendaSemAluguel);
      const impostoLiquidoSemAluguel = reducaoSemAluguel === Infinity ? 0 : Math.max(0, impostoBrutoSemAluguel - reducaoSemAluguel);

      // Imposto do aluguel pela diferença (captura benefícios da redução)
      const impostoPelaDiferenca = Math.max(0, impostoLiquidoTotal - impostoLiquidoSemAluguel);

      // Alíquota marginal pura (teto máximo - evita cliff effect)
      const impostoMarginalPuro = monthlyRent * getAliquotaMarginal(totalIncomeWithRent);

      // Usar o menor valor: beneficia o contribuinte quando há redução,
      // mas nunca ultrapassa a alíquota marginal pura
      monthlyTax = Math.min(impostoPelaDiferenca, impostoMarginalPuro);
    }
  } else {
    // Aluguel é a única renda: aplicar tabela progressiva com isenção/redução 2026
    const impostoBruto = calcularImpostoBruto(monthlyRent);
    const reducao = calcularReducao2026(monthlyRent);
    monthlyTax = reducao === Infinity ? 0 : Math.max(0, impostoBruto - reducao);
  }

  const yearlyTax = monthlyTax * 12;
  const effectiveRate = monthlyRent > 0 ? (monthlyTax / monthlyRent) * 100 : 0;

  return { monthlyTax, yearlyTax, effectiveRate };
}

function calculatePJ(monthlyIncome: number): TaxResult {
  if (monthlyIncome <= 0) {
    return { monthlyTax: 0, yearlyTax: 0, effectiveRate: 0 };
  }

  const lucroPresumido = monthlyIncome * PJ_RATES.presumption;

  const irpj = lucroPresumido * PJ_RATES.irpj;
  const csll = lucroPresumido * PJ_RATES.csll;
  const pis = monthlyIncome * PJ_RATES.pis;
  const cofins = monthlyIncome * PJ_RATES.cofins;

  const monthlyTax = irpj + csll + pis + cofins;
  const yearlyTax = monthlyTax * 12;
  const effectiveRate = (monthlyTax / monthlyIncome) * 100;

  return { monthlyTax, yearlyTax, effectiveRate };
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

function formatPercent(value: number): string {
  return value.toFixed(1).replace('.', ',') + '%';
}

function parseInputValue(value: string): number {
  // Remove tudo exceto números
  const numbers = value.replace(/\D/g, '');
  // Converte para número (últimos 2 dígitos são centavos)
  return numbers ? parseInt(numbers, 10) / 100 : 0;
}

function formatInputValue(value: number): string {
  if (value === 0) return '';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export default function TaxCalculator() {
  const [rentValue, setRentValue] = useState<string>('');
  const [totalIncomeValue, setTotalIncomeValue] = useState<string>('');
  const [results, setResults] = useState<Results | null>(null);

  const handleRentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const numericValue = parseInputValue(rawValue);
    setRentValue(formatInputValue(numericValue));
  };

  const handleTotalIncomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const numericValue = parseInputValue(rawValue);
    setTotalIncomeValue(formatInputValue(numericValue));
  };

  const handleCalculate = () => {
    const rent = parseInputValue(rentValue);
    if (rent <= 0) return;

    const totalIncome = parseInputValue(totalIncomeValue);
    const effectiveTotalIncome = totalIncome >= rent ? totalIncome : undefined;

    const pf = calculatePF(rent, effectiveTotalIncome);
    const pj = calculatePJ(rent);
    const savings = pf.yearlyTax - pj.yearlyTax;
    const savingsTier = getSavingsTier(savings);

    trackCalculatorSubmit(Boolean(effectiveTotalIncome), savingsTier);
    if (savings > 0) {
      trackCalculatorResultShown(savingsTier);
    }

    setResults({ pf, pj, savings });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCalculate();
    }
  };

  return (
    <div className="space-y-8">

      {/* Input Section */}
      <Card className="border-navy-200 bg-white shadow-sm">
        <CardContent className="pt-6">
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Campo de Aluguel */}
              <div className="space-y-2">
                <Label htmlFor="rent" className="text-base font-medium text-navy-700">
                  Renda Mensal de Aluguéis
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 font-medium">
                    R$
                  </span>
                  <Input
                    id="rent"
                    type="text"
                    inputMode="numeric"
                    placeholder="0,00"
                    value={rentValue}
                    onChange={handleRentChange}
                    onKeyDown={handleKeyDown}
                    className="pl-10 text-lg h-12"
                  />
                </div>
                <p className="text-sm text-neutral-500">
                  Valor total mensal de aluguéis
                </p>
              </div>

              {/* Campo de Renda Tributável Total */}
              <div className="space-y-2">
                <Label htmlFor="totalIncome" className="text-base font-medium text-navy-700">
                  Renda Tributável Mensal Total
                  <span className="text-neutral-400 text-sm font-normal ml-1">(opcional)</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 font-medium">
                    R$
                  </span>
                  <Input
                    id="totalIncome"
                    type="text"
                    inputMode="numeric"
                    placeholder="0,00"
                    value={totalIncomeValue}
                    onChange={handleTotalIncomeChange}
                    onKeyDown={handleKeyDown}
                    className="pl-10 text-lg h-12"
                  />
                </div>
                <p className="text-sm text-neutral-500">
                  Inclui salário, aposentadoria, aluguel e outras rendas
                </p>
              </div>
            </div>

            {/* Nota explicativa */}
            <div className="bg-navy-50 border border-navy-200 rounded-lg p-4 text-sm text-navy-800">
              <p className="font-medium mb-1">Por que informar a renda total?</p>
              <p>
                O imposto de renda é progressivo e a sua renda afeta o imposto sobre o aluguel. Se você já tem outras rendas tributáveis,
                o aluguel será tributado na sua alíquota marginal (a mais alta). Deixe em
                branco se o aluguel é a sua única renda.
              </p>
            </div>

            <Button
              onClick={handleCalculate}
              className="w-full h-12 text-base bg-gold-500 text-white shadow-lg transition-colors hover:bg-gold-600 disabled:opacity-60"
              disabled={false}
            >
              <Calculator className="w-5 h-5 mr-2" />
              Calcular Economia
            </Button>


          </div>
        </CardContent>
      </Card>

      {/* Results Section */}
      {results && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {/* Comparison Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* PF Card */}
            <Card className="border-navy-200 bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-navy-900">
                  <User className="w-5 h-5 text-gold-600" />
                  Pessoa Física
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-neutral-500">Imposto Mensal</p>
                  <p className="text-2xl font-bold text-navy-900">
                    {formatCurrency(results.pf.monthlyTax)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Imposto Anual</p>
                  <p className="text-xl font-semibold text-navy-700">
                    {formatCurrency(results.pf.yearlyTax)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Alíquota Efetiva</p>
                  <p className="text-lg font-semibold text-gold-600">
                    {formatPercent(results.pf.effectiveRate)}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* PJ Card */}
            <Card className="border-navy-200 bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-gold-600">
                  <Building2 className="w-5 h-5" />
                  Holding (PJ)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-neutral-500">Imposto Mensal</p>
                  <p className="text-2xl font-bold text-navy-900">
                    {formatCurrency(results.pj.monthlyTax)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Imposto Anual</p>
                  <p className="text-xl font-semibold text-navy-700">
                    {formatCurrency(results.pj.yearlyTax)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Alíquota Efetiva</p>
                  <p className="text-lg font-semibold text-gold-600">
                    {formatPercent(results.pj.effectiveRate)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {results.pf.monthlyTax === 0 && (
            <div className="rounded-2xl border border-navy-300 bg-gradient-to-r from-navy-900/60 to-navy-800/60 p-4 text-sm text-neutral-100 shadow-lg">
              <p className="max-w-2xl text-xs font-semibold uppercase tracking-wide text-neutral-200">Contexto da isenção</p>
              <p className="mt-1 text-sm text-neutral-100">
                Caso você não informe sua renda tributável mensal total (incluindo salário, aposentadoria e aluguel), consideramos que ela é composta apenas do aluguel e a isenção de até R$ 5.000 aplica-se sobre a renda tributável total completa. Por isso, é fundamental você informar a renda total para ter uma simulação mais realista.
              </p>
            </div>
          )}
          {/* Savings Highlight */}
          {results.savings > 0 && (
            <Card className="border-gold-300 bg-gradient-to-r from-navy-900 to-navy-800 text-white">
              <CardContent className="py-5">
                <div className="grid gap-4 md:grid-cols-[repeat(3,minmax(0,1fr))]">
                  <div className="flex flex-col gap-3 rounded-lg bg-white/10 p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-200/40 rounded-full flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div>
                        <p className="text-neutral-300 text-xs font-bold uppercase tracking-wide">Economia Anual</p>
                        <p className="text-2xl font-bold text-emerald-400">{formatCurrency(results.savings)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3 rounded-lg bg-white/10 p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-200/40 rounded-full flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div>
                        <p className="text-neutral-300 text-xs font-bold uppercase tracking-wide">Economia em 10 anos</p>
                        <p className="text-2xl font-bold text-emerald-400">{formatCurrency(results.savings * 10)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center rounded-lg bg-white/10 p-3">
                    <a
                      href="#contato"
                      onClick={() => trackContactClick('whatsapp', 'calculator_result')}
                      className="w-full text-center rounded-lg border border-gold-400 bg-gold-500/90 px-4 py-3 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-gold-600"
                    >
                      Quero Economizar
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Disclaimer */}
          <p className="text-xs text-neutral-500 text-center">
            * Simulação baseada nas alíquotas de IRPF 2026 (Lei 15.270/25) com isenção até R$ 5.000 e redução
            progressiva até R$ 7.350, e regime de Lucro Presumido para holdings patrimoniais.
            Os valores são estimativas e podem variar conforme sua situação específica. Consulte um especialista.
          </p>
        </div>
      )}
    </div>
  );
}
